import React, { useState } from 'react';

const WordNumberPuzzleGame = ({ gameData, onComplete }) => {
    const [answers, setAnswers] = useState({});
    const [score, setScore] = useState(0);
    const [checkedClues, setCheckedClues] = useState([]);
    const [feedback, setFeedback] = useState(null); 
    const [showHints, setShowHints] = useState({});

    const totalClues = gameData?.clues?.length || 0;
    const pointsPerClue = gameData?.points_per_clue || 20;

    const normalizeAnswer = (text) => {
        if (!text) return '';
        return text.toString().trim().toLowerCase().replace(/\s+/g, '');
    };

    const handleAnswerChange = (clueId, value) => {
        setAnswers({ ...answers, [clueId]: value });
    };

    const checkAnswer = (clue) => {
        if (checkedClues.includes(clue.id) || feedback) return;

        const userAnswer = normalizeAnswer(answers[clue.id] || '');
        const correctAnswer = normalizeAnswer(clue.answer);

        if (userAnswer === correctAnswer) {
            const newScore = score + pointsPerClue;
            setScore(newScore);
            const newChecked = [...checkedClues, clue.id];
            setCheckedClues(newChecked);
            setFeedback({ status: 'correct', clue });
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);

            setTimeout(() => {
                setFeedback(null);
                if (newChecked.length === totalClues) {
                    if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                }
            }, 4000);
        } else {
            setFeedback({ status: 'wrong', clue });
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
            setTimeout(() => setFeedback(null), 1500);
        }
    };

    const translateDifficulty = (diff) => {
        const trans = { 'easy': 'Lengvas', 'medium': 'Vidutinis', 'hard': 'Sunkus' };
        return trans[diff?.toLowerCase()] || diff;
    };

    if (!gameData || !gameData.clues) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-teal-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🧩</div>
                <h3 className="text-base md:text-3xl font-black text-teal-900 tracking-tighter uppercase">Generuojami galvosūkiai...</h3>
            </div>
        );
    }

    const progress = (checkedClues.length / totalClues) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Logic HUD */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-teal-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-2 md:p-6">
                        <div className="w-20 h-20 bg-teal-100 rounded-3xl flex items-center justify-center text-lg md:text-4xl shadow-inner border-2 border-teal-200 animate-float">
                            🧩
                        </div>
                        <div>
                            <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                                Loginės <span className="text-teal-600">Mįslės</span>
                            </h3>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="bg-teal-600 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase shadow-lg">
                                    {translateDifficulty(gameData.difficulty)}
                                </span>
                                <p className="text-slate-500 font-bold italic font-serif">
                                    Išspręsk mįsles ir atrakink paslėptus atsakymus!
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 md:p-6">
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">IQ Taškai</p>
                            <p className="text-base md:text-3xl font-black text-teal-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[120px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Mįslės</p>
                            <p className="text-base md:text-3xl font-black text-slate-800 leading-none">{checkedClues.length}/{totalClues}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 w-full bg-slate-200/50 rounded-full h-4 p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-teal-400 via-cyan-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(20,184,166,0.4)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Main Logic Interaction Stage */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:p-10 relative">
                {gameData.clues.map((clue, idx) => {
                    const isChecked = checkedClues.includes(clue.id);
                    const isWrong = feedback?.status === 'wrong' && feedback?.clue?.id === clue.id;

                    return (
                        <div
                            key={clue.id}
                            className={`
                                premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-14 shadow-2xl transition-all duration-700 transform-gpu relative overflow-hidden border-4
                                ${isChecked 
                                    ? 'bg-emerald-500/10 border-emerald-400/50 scale-95 opacity-60 grayscale-[0.5]' 
                                    : (isWrong ? 'bg-rose-500/10 border-rose-400/50 animate-shake shadow-[0_0_60px_rgba(244,63,94,0.2)]' : 'bg-white/60 border-white/80 hover:bg-white hover:scale-[1.02] shadow-[0_30px_60px_rgba(0,0,0,0.05)]')}
                            `}
                        >
                            {/* Tactical Type Badge & Advanced Emoji */}
                            <div className="flex items-center justify-between mb-10">
                                <span className={`
                                    px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-[0.3em] shadow-lg border-b-4 transform transition-all duration-500
                                    ${clue.type === 'math'
                                        ? 'bg-blue-600 text-white border-blue-800'
                                        : 'bg-purple-600 text-white border-purple-800'}
                                    ${isChecked ? 'opacity-40 grayscale' : ''}
                                `}>
                                    {clue.type === 'math' ? '🔢 MATEMATIKA' : '📝 ŽODYNAS'}
                                </span>
                                <div className="text-8xl lg:text-9xl drop-shadow-[0_20px_40px_rgba(0,0,0,0.1)] animate-float" style={{ animationDelay: `${idx * 0.3}s` }}>
                                    {clue.emoji}
                                </div>
                            </div>

                            {/* Advanced Clue Card */}
                            <div className="min-h-[20vh] flex items-center mb-12 bg-white/40 p-3 md:p-8 rounded-xl md:rounded-[3rem] border-2 border-teal-50 relative group">
                                <div className="absolute -top-4 left-6 px-4 bg-teal-600 text-white text-[8px] font-black rounded-full uppercase tracking-widest">Mįslė #{idx + 1}</div>
                                <p className="text-base md:text-3xl lg:text-lg md:text-4xl font-black text-slate-900 leading-[1.2] tracking-tighter italic font-serif">
                                    "{clue.clue}"
                                </p>
                            </div>

                            {/* Cipher Interaction Area */}
                            {!isChecked ? (
                                <div className="space-y-3 md:space-y-8 animate-slide-up">
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            value={answers[clue.id] || ''}
                                            onChange={(e) => handleAnswerChange(clue.id, e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && checkAnswer(clue)}
                                            placeholder="Įvesk atsakymą..."
                                            disabled={!!feedback}
                                            className={`
                                                w-full p-4 md:p-10 text-base md:text-3xl font-black rounded-xl md:rounded-[3rem] border-4 focus:outline-none transition-all shadow-2xl text-center uppercase tracking-widest font-mono
                                                ${isWrong ? 'border-rose-400 bg-rose-50 text-rose-900' : 'border-slate-100 bg-white/80 focus:border-teal-500 focus:bg-white focus:scale-[1.02] text-slate-800'}
                                            `}
                                        />
                                        <button
                                            onClick={() => { if (navigator.vibrate) navigator.vibrate(20); setShowHints({ ...showHints, [clue.id]: !showHints[clue.id] }); }}
                                            className={`absolute right-6 top-1/2 -translate-y-1/2 w-16 h-16 rounded-2xl flex items-center justify-center text-base md:text-3xl transition-all shadow-xl active:scale-95 ${showHints[clue.id] ? 'bg-amber-500 text-white scale-110' : 'bg-white text-amber-500 border-2 border-amber-50'}`}
                                        >
                                            💡
                                        </button>
                                    </div>

                                    <button
                                        onClick={() => checkAnswer(clue)}
                                        disabled={!answers[clue.id] || !!feedback}
                                        className="premium-button-3d w-full py-4 md:py-10 bg-gradient-to-r from-teal-500 via-cyan-600 to-indigo-600 text-white rounded-xl md:rounded-[3rem] font-black text-base md:text-3xl shadow-[0_20px_50px_rgba(20,184,166,0.3)] transition-all border-b-[12px] border-teal-800 uppercase tracking-[0.2em] flex items-center justify-center gap-2 md:p-6 disabled:opacity-20 transform-gpu active:translate-y-4 active:border-b-0"
                                    >
                                        <span>TIKRINTI</span>
                                        <span className="text-lg md:text-4xl animate-bounce">✓</span>
                                    </button>

                                    {showHints[clue.id] && clue.hint && (
                                        <div className="bg-amber-50 rounded-xl md:rounded-[3rem] p-3 md:p-8 border-4 border-amber-100 text-slate-800 font-bold italic animate-pop-in text-2xl text-center shadow-inner relative overflow-hidden font-serif">
                                            <div className="absolute top-0 right-0 p-4 opacity-5 text-2xl md:text-6xl font-black select-none text-amber-500">💡</div>
                                            "{clue.hint}"
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-emerald-600 text-white rounded-[3.5rem] p-4 md:p-12 text-center animate-pop-in shadow-[0_20px_50px_rgba(16,185,129,0.3)] border-b-[16px] border-emerald-800 relative overflow-hidden group">
                                    <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity animate-pulse" />
                                    <div className="text-[10px] font-black uppercase tracking-[0.5em] mb-4 opacity-80 leading-none">IŠŠIFRUOTA SĖKMINGAI</div>
                                    <div className="text-2xl md:text-6xl lg:text-7xl font-black tracking-widest uppercase drop-shadow-lg font-mono relative z-10">
                                        {clue.answer}
                                    </div>
                                    <div className="absolute -bottom-6 -right-6 text-9xl opacity-10 rotate-12 select-none group-hover:scale-125 transition-transform">💎</div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Immersive Cipher Success Overlay */}
            {feedback?.status === 'correct' && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-3xl animate-fade-in" />
                    <div className="bg-white rounded-3xl md:rounded-[5rem] p-4 md:p-20 max-w-3xl w-full shadow-[0_60px_120px_rgba(0,0,0,0.6)] border-[16px] border-teal-500 relative animate-pop-in text-center overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(20,184,166,0.05)_0%,transparent_100%)] pointer-events-none" />
                        <div className="absolute -top-4 md:p-20 left-1/2 -translate-x-1/2 w-40 h-40 bg-white rounded-full flex items-center justify-center text-lg md:text-4xl md:text-[100px] shadow-2xl border-[10px] border-teal-500 animate-bounce">
                            🎯
                        </div>
                        
                        <div className="space-y-4 md:space-y-12 pt-10 relative z-10">
                            <h2 className="text-2xl md:text-6xl font-black text-slate-950 tracking-tighter uppercase leading-none italic font-serif">PUIDUS ATSAKYMAS!</h2>
                            
                            <div className="bg-teal-50 rounded-2xl md:rounded-[4rem] p-4 md:p-14 border-4 border-teal-100 shadow-inner group">
                                <p className="text-[12px] font-black text-teal-400 uppercase tracking-[0.6em] mb-6 leading-none">Atskleista mįslė:</p>
                                <p className="text-7xl lg:text-9xl font-black text-teal-900 tracking-[0.1em] uppercase leading-none font-mono drop-shadow-sm group-hover:scale-110 transition-transform">
                                    {feedback.clue.answer}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-2 md:p-6">
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                            </div>
                            <p className="text-2xl text-slate-500 font-bold italic font-serif">Ruošiama kita loginė užduotis...</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WordNumberPuzzleGame;
